import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { contentApi } from '@/services/adminApi';
import { useEntity, useMutation } from '@/hooks';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { ArrowLeft, Edit, Trash2 } from 'lucide-react';
import { LoadingState, ErrorState, CopyableId } from '@/components/common';
import type { Content } from '@/types/admin';

export const ContentDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { hasPermission } = useAdminAuth();

  const canEdit = hasPermission('edit', 'content');
  const canDelete = hasPermission('delete', 'content');

  // Use entity hook for fetching content
  const {
    data: content,
    isLoading,
    error,
    refetch,
  } = useEntity<Content>({
    fetchFn: (contentId: string) => contentApi.getById(contentId),
    entityId: id,
    entityName: 'content',
    enabled: !!id,
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error('Content ID is required');
      await contentApi.delete(id);
    },
    entityName: 'content',
    operation: 'delete',
    onSuccess: () => {
      window.location.href = '/admin/contents';
    },
  });

  const handleDelete = async () => {
    if (!id || !window.confirm('Are you sure you want to delete this content?')) return;
    await deleteMutation.mutate();
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'N/A';
    const kb = bytes / 1024;
    const mb = kb / 1024;
    if (mb >= 1) return `${mb.toFixed(2)} MB`;
    return `${kb.toFixed(2)} KB`;
  };

  const isRecord = (value: unknown): value is Record<string, unknown> =>
    !!value && typeof value === 'object' && !Array.isArray(value);

  const recordId = content?.id || id || '';
  const metadata = content?.metadata;
  const termsContent = isRecord(metadata) && isRecord(metadata.termsContent) ? metadata.termsContent : null;
  const termsIntro = termsContent && isRecord(termsContent.introduction) ? termsContent.introduction : null;
  const termsSections =
    termsContent && Array.isArray(termsContent.sections) ? (termsContent.sections as unknown[]) : [];
  const termsFooter = termsContent && isRecord(termsContent.footer) ? termsContent.footer : null;

  if (isLoading) {
    return <LoadingState message="Loading content..." />;
  }

  if (error || !content) {
    return <ErrorState error={error || new Error('Content not found')} onRetry={refetch} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/admin/contents">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{content.title}</h1>
            <p className="text-muted-foreground mt-2">Content Details</p>
          </div>
        </div>
        <div className="flex gap-2">
          {canEdit && (
            <Button asChild>
              <Link to={`/admin/contents/${id}/edit`}>
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </Link>
            </Button>
          )}
          {canDelete && (
            <Button variant="destructive" onClick={handleDelete} disabled={deleteMutation.isLoading}>
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {content.contentId && <CopyableId id={content.contentId} label="Content ID" />}
            <CopyableId id={recordId} label="Record ID" />
            <div>
              <label className="text-sm font-medium text-muted-foreground">Title</label>
              <p className="mt-1">{content.title}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Description</label>
              <p className="mt-1">{content.description || 'N/A'}</p>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Type</label>
                <p className="mt-1">{content.type}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Content For</label>
                <p className="mt-1">{content.contentFor || 'N/A'}</p>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Status</label>
              <p className="mt-1">
                <span
                  className={`px-2 py-1 rounded-full text-xs ${
                    content.status === 'PUBLISHED'
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      : content.status === 'DRAFT'
                      ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                      : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                  }`}
                >
                  {content.status}
                </span>
              </p>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Category</label>
                <p className="mt-1">{content.category || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Featured</label>
                <p className="mt-1">{content.featured ? 'Yes' : 'No'}</p>
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Access Count</label>
                <p className="mt-1">{content.accessCount ?? 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Version</label>
                <p className="mt-1">{content.version ?? 'N/A'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>File Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">File Name</label>
              <p className="mt-1">{content.fileName || 'N/A'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">File Size</label>
              <p className="mt-1">{formatFileSize(content.fileSize)}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">MIME Type</label>
              <p className="mt-1">{content.mimeType || 'N/A'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Content Format</label>
              <p className="mt-1">{content.contentFormat || 'N/A'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Cloud Provider</label>
              <p className="mt-1">{content.cloudProvider || 'N/A'}</p>
            </div>
            {content.fileUrl && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">File URL</label>
                <p className="mt-1 break-all">
                  <a
                    href={content.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    {content.fileUrl}
                  </a>
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {content.tags && content.tags.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Tags</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {content.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-secondary text-secondary-foreground rounded-md text-sm"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {metadata && Object.keys(metadata).length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Metadata</CardTitle>
              <CardDescription>Additional content metadata and structured fields</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-sm text-muted-foreground">
                {Object.keys(metadata).length} keys
              </div>

              {isRecord(metadata) && (
                <div className="grid gap-4 md:grid-cols-2">
                  {'lastUpdated' in metadata && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Last Updated</label>
                      <p className="mt-1 text-sm">{String(metadata.lastUpdated)}</p>
                    </div>
                  )}
                  {'effectiveDate' in metadata && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Effective Date</label>
                      <p className="mt-1 text-sm">{String(metadata.effectiveDate)}</p>
                    </div>
                  )}
                  {'version' in metadata && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Metadata Version</label>
                      <p className="mt-1 text-sm">{String(metadata.version)}</p>
                    </div>
                  )}
                  {'documentType' in metadata && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Document Type</label>
                      <p className="mt-1 text-sm">{String(metadata.documentType)}</p>
                    </div>
                  )}
                  {'jurisdiction' in metadata && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Jurisdiction</label>
                      <p className="mt-1 text-sm">{String(metadata.jurisdiction)}</p>
                    </div>
                  )}
                </div>
              )}

              {isRecord(metadata) && Array.isArray(metadata.complianceFrameworks) && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Compliance Frameworks</label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {metadata.complianceFrameworks.map((f, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200 text-xs"
                      >
                        {String(f)}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {isRecord(metadata) && Array.isArray(metadata.languages) && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Languages</label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {metadata.languages.map((lang, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 text-xs"
                      >
                        {String(lang)}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-muted-foreground">Raw Metadata</label>
                <pre className="mt-2 text-xs bg-muted/50 p-3 rounded-lg overflow-auto max-h-[420px]">
                  {JSON.stringify(metadata, null, 2)}
                </pre>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Audit Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Created At</label>
              <p className="mt-1">
                {content.createdAt ? new Date(content.createdAt).toLocaleString() : 'N/A'}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Updated At</label>
              <p className="mt-1">
                {content.updatedAt ? new Date(content.updatedAt).toLocaleString() : 'N/A'}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Created By</label>
              <p className="mt-1">{content.createdBy || 'N/A'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Last Modified By</label>
              <p className="mt-1">{content.lastModifiedBy || 'N/A'}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Structured Document Content (from metadata) */}
      {(termsIntro || termsSections.length > 0 || termsFooter) && (
        <Card>
          <CardHeader>
            <CardTitle>Document Content</CardTitle>
            <CardDescription>Structured sections pulled from content metadata</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {termsIntro && (
              <div className="border rounded-lg p-4 space-y-2">
                {'title' in termsIntro && (
                  <h3 className="font-semibold">{String(termsIntro.title)}</h3>
                )}
                {'text' in termsIntro && (
                  <p className="text-sm whitespace-pre-wrap">{String(termsIntro.text)}</p>
                )}
              </div>
            )}

            {termsSections.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-muted-foreground">
                  Sections ({termsSections.length})
                </h3>
                <div className="grid gap-4 md:grid-cols-2">
                  {termsSections.map((section, idx) => {
                    const sec = isRecord(section) ? section : null;
                    const sectionId = sec && typeof sec.id === 'string' ? sec.id : undefined;
                    const sectionTitle = sec && typeof sec.title === 'string' ? sec.title : `Section ${idx + 1}`;
                    const sectionContent = sec && typeof sec.content === 'string' ? sec.content : '';
                    return (
                      <div key={sectionId || idx} className="border rounded-lg p-4 space-y-3">
                        <div className="flex items-start justify-between gap-3">
                          <h4 className="font-semibold">{sectionTitle}</h4>
                        </div>
                        {sectionId && <CopyableId id={sectionId} label="Section ID" />}
                        {sectionContent && (
                          <p className="text-sm whitespace-pre-wrap text-muted-foreground">{sectionContent}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {termsFooter && (
              <div className="border rounded-lg p-4 space-y-2">
                {'title' in termsFooter && (
                  <h3 className="font-semibold">{String(termsFooter.title)}</h3>
                )}
                {'content' in termsFooter && (
                  <p className="text-sm whitespace-pre-wrap">{String(termsFooter.content)}</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Media Preview */}
      {content.fileUrl && (
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle>Preview</CardTitle>
            <CardDescription>Media preview and download</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {content.type === 'IMAGE' ? (
              <div className="flex flex-col items-center gap-4">
                <div className="relative w-full max-w-2xl border rounded-lg overflow-hidden bg-muted">
                  <img
                    src={content.fileUrl}
                    alt={content.title}
                    className="w-full h-auto object-contain max-h-[600px]"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%23ddd" width="400" height="300"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="20" dy="10.5" font-weight="bold" x="50%25" y="50%25" text-anchor="middle"%3EImage not available%3C/text%3E%3C/svg%3E';
                    }}
                  />
                </div>
                <Button asChild variant="outline">
                  <a href={content.fileUrl} download={content.fileName || content.title} target="_blank" rel="noopener noreferrer">
                    Download Image
                  </a>
                </Button>
              </div>
            ) : content.type === 'VIDEO' ? (
              <div className="flex flex-col items-center gap-4">
                <div className="w-full max-w-2xl border rounded-lg overflow-hidden bg-muted">
                  <video
                    src={content.fileUrl}
                    controls
                    className="w-full h-auto max-h-[600px]"
                    onError={(e) => {
                      const target = e.target as HTMLVideoElement;
                      target.style.display = 'none';
                      const errorDiv = document.createElement('div');
                      errorDiv.className = 'p-8 text-center text-muted-foreground';
                      errorDiv.textContent = 'Video preview not available';
                      target.parentElement?.appendChild(errorDiv);
                    }}
                  >
                    Your browser does not support the video tag.
                  </video>
                </div>
                <Button asChild variant="outline">
                  <a href={content.fileUrl} download={content.fileName || content.title} target="_blank" rel="noopener noreferrer">
                    Download Video
                  </a>
                </Button>
              </div>
            ) : content.type === 'AUDIO' ? (
              <div className="flex flex-col items-center gap-4">
                <div className="w-full max-w-md border rounded-lg p-4 bg-muted">
                  <audio src={content.fileUrl} controls className="w-full">
                    Your browser does not support the audio tag.
                  </audio>
                </div>
                <Button asChild variant="outline">
                  <a href={content.fileUrl} download={content.fileName || content.title} target="_blank" rel="noopener noreferrer">
                    Download Audio
                  </a>
                </Button>
              </div>
            ) : content.type === 'DOCUMENT' ? (
              <div className="flex flex-col items-center gap-4">
                <div className="w-full max-w-md border rounded-lg p-8 bg-muted text-center">
                  <p className="text-lg font-semibold mb-2">{content.fileName || 'Document'}</p>
                  <p className="text-sm text-muted-foreground mb-4">
                    {content.mimeType || 'Document file'}
                  </p>
                  <Button asChild>
                    <a href={content.fileUrl} download={content.fileName || content.title} target="_blank" rel="noopener noreferrer">
                      Download Document
                    </a>
                  </Button>
                </div>
              </div>
            ) : (
              <div className="border rounded-lg p-4 bg-muted">
                <p className="text-sm text-muted-foreground mb-2">File URL:</p>
                <a
                  href={content.fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline break-all"
                >
                  {content.fileUrl}
                </a>
                {content.fileName && (
                  <Button asChild variant="outline" className="mt-4">
                    <a href={content.fileUrl} download={content.fileName} target="_blank" rel="noopener noreferrer">
                      Download File
                    </a>
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ContentDetailPage;


